// ============================================================
// Standortmonitor — Library — Slice Service
// Builds a data slice from a dashboard's live context (DashboardDataController):
// derives title/subtitle, reduces transformed chart data to a lightweight
// preview series, and computes the dedupe signature. Also wraps toggling
// (plus tool) including toast notification.
// ============================================================

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { MessageService } from 'primeng/api';
import { DSVRowArray } from 'd3';
import { LibrarySlicePersistenceService } from './library-slice-persistence.service';
import { SliceFilterRestoreService } from './slice-filter-restore.service';
import { DataSlice, SlicePreviewSeries } from 'src/app/state/library/library.model';
import { LibraryState } from 'src/app/state/library/library.state';
import { sliceSignature } from 'src/app/state/library/library.util';
import { AuthState } from 'src/app/state/auth/auth.state';
import { ProductsState } from 'src/app/state/products/products.state';
import { resolveSliceProductId } from './slice-product';
import {
  resolveSlicePreviewSub,
  resolveSlicePreviewTitle,
} from '../preview/slice-definition-shell';
import { SlicePreviewBuildersService } from '../preview/slice-preview-builders.service';
import { LocalDatasetsService } from 'src/app/shared/services/local-datasets.service';

export type SliceMode = 'live' | 'snapshot';

/** Raw context of an analysis view at save time. */
export interface SliceCaptureContext {
  dashboardId: string;
  dashboardVariant?: string;
  /** Shell package id from the live definition (`definition.shellId`). */
  shellId?: string;
  datasetName: string;
  datasetBuiltAt: string;
  /** Analysis heading (e.g. from chartHeading$). */
  heading: string;
  /** Current filter values (Map → serialized). */
  filterValues: Map<string, unknown>;
  /** Transformed chart data for preview reduction. */
  data: DSVRowArray | null;
  /** Live-Slice vs Snapshot; defaults to live. */
  mode?: SliceMode;
}

/** Labels for multi-select filters used in the subtitle. */
const MULTISELECT_LABELS: Record<string, string> = {
  states: 'Bundesländer',
  sub_markets: 'Teilmärkte',
  districts: 'Kreise',
  wz_classes: 'WZ-Klassen',
  industries: 'Branchen',
};

@Injectable({ providedIn: 'root' })
export class LibrarySliceService {

  constructor(
    private store: Store,
    private messageService: MessageService,
    private persistence: LibrarySlicePersistenceService,
    private router: Router,
    private sliceFilterRestore: SliceFilterRestoreService,
    private previewBuilders: SlicePreviewBuildersService,
    private localDatasetsService: LocalDatasetsService,
  ) {}

  /** Compute signature for a context (dedupe key). */
  signatureFor(ctx: SliceCaptureContext): string {
    return sliceSignature({
      dashboardId: ctx.dashboardId,
      dashboardVariant: ctx.dashboardVariant,
      datasetName: ctx.datasetName,
      filterValues: this._serializeFilterValues(ctx.filterValues),
      live: (ctx.mode ?? 'live') === 'live',
    });
  }

  /** Is a slice already saved for this context? */
  isSaved(ctx: SliceCaptureContext): boolean {
    return this.store.selectSnapshot(LibraryState.signatures).includes(this.signatureFor(ctx));
  }

  /** Build a complete data slice from the context. */
  buildSlice(ctx: SliceCaptureContext): DataSlice {
    const live = (ctx.mode ?? 'live') === 'live';
    const filterValues = this._serializeFilterValues(ctx.filterValues);
    const previewSeries = this.buildPreviewSeries(
      ctx.data,
      ctx.dashboardId,
      ctx.filterValues,
      ctx.shellId,
    );
    return {
      id: this._generateId(),
      signature: this.signatureFor(ctx),
      title: this._deriveTitle(ctx),
      sub: this._deriveSub(ctx, previewSeries),
      dashboardId: ctx.dashboardId,
      dashboardVariant: ctx.dashboardVariant,
      shellId: ctx.shellId,
      datasetName: ctx.datasetName,
      datasetBuiltAt: ctx.datasetBuiltAt,
      filterValues,
      previewSeries,
      live,
      fav: false,
      createdAt: new Date().toISOString(),
    };
  }

  /**
   * Plus tool: toggle slice. If the signature exists, remove it;
   * otherwise add it. Shows a toast notification.
   */
  toggle(ctx: SliceCaptureContext): { added: boolean } {
    const added = !this.isSaved(ctx);
    const mode = ctx.mode ?? 'live';
    const kind = mode === 'snapshot' ? 'Snapshot' : 'Live-Slice';
    const slice = this.buildSlice(ctx);

    this.persistence.toggle(slice).subscribe({
      next: ({ added }) => {
        this.messageService.add({
          severity: added ? 'success' : 'info',
          summary: added ? `Diagramm als ${kind} gespeichert` : `Diagramm-${kind} entfernt`,
          detail: added
            ? 'Der Datenslice erscheint in der Bibliothek.'
            : 'Der Datenslice wurde aus der Bibliothek entfernt.',
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Speichern fehlgeschlagen',
          detail: this.persistence.isApiMode
            ? 'Der Datenslice konnte nicht mit dem Server synchronisiert werden.'
            : 'Der Datenslice konnte nicht gespeichert werden.',
        });
      },
    });

    return { added };
  }

  /** Load the user's saved slices (API mode) so "already saved?" checks are accurate. */
  ensureLoaded(): void {
    this.persistence.ensureLoaded().subscribe({
      error: (error) => console.warn('[LibrarySlice] Loading saved slices failed', error),
    });
  }

  removeSlice(sliceId: string): void {
    this.persistence.remove(sliceId).subscribe({
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Löschen fehlgeschlagen',
          detail: 'Der Datenslice konnte nicht entfernt werden.',
        });
      },
    });
  }

  toggleFavorite(sliceId: string): void {
    this.persistence.toggleFavorite(sliceId).subscribe({
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Favorit konnte nicht gespeichert werden',
        });
      },
    });
  }

  /** Navigate to the slice's analysis and restore its saved filter values. */
  openAnalysis(selectedProductId: number | string, slice: DataSlice): void {
    const productId = resolveSliceProductId(
      slice.dashboardId,
      Number(selectedProductId),
      this.store.selectSnapshot(ProductsState.accessibleProducts).map((product) => product.id),
      (claims) => this.store.selectSnapshot(AuthState.hasClaims(claims)),
    );
    const commands: (string | number)[] = ['/dashboards', productId, slice.dashboardId];
    if (slice.dashboardVariant) {
      commands.push(slice.dashboardVariant);
    }

    this.sliceFilterRestore.stage(slice);
    this.router.navigate(commands).then((success) => {
      if (!success) {
        this.sliceFilterRestore.clear();
      }
    });
  }

  /**
   * Reduce transformed chart data to a preview series.
   * Dedicated builders live in SlicePreviewBuildersService.
   */
  buildPreviewSeries(
    data: DSVRowArray | null,
    dashboardId: string,
    filterValues?: Map<string, unknown> | Record<string, unknown>,
    shellId?: string,
  ): SlicePreviewSeries[] {
    return this.previewBuilders.buildPreviewSeries(data, dashboardId, filterValues, shellId);
  }

  // ---------- internal ----------

  private _generateId(): string {
    return `sl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  }

  /** Map → plain JSON-serializable object. */
  private _serializeFilterValues(filterValues: Map<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    filterValues.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }

  /** Derive title from shell previewTitle / heading subject, else chart heading. */
  private _deriveTitle(ctx: SliceCaptureContext): string {
    const fromPreview = resolveSlicePreviewTitle({
      shellId: ctx.shellId,
      dashboardId: ctx.dashboardId,
      dashboardVariant: ctx.dashboardVariant,
      filterValues: this._serializeFilterValues(ctx.filterValues),
    });
    if (fromPreview) {
      return fromPreview;
    }
    const trimmed = (ctx.heading ?? '').trim();
    if (!trimmed) {
      return 'Gespeicherte Ansicht';
    }
    return trimmed.split('|')[0].trim();
  }

  /**
   * Subtitle: shell registry previewSub when present
   * else time range + dominant multi-select.
   */
  private _deriveSub(
    ctx: SliceCaptureContext,
    previewSeries: SlicePreviewSeries[],
  ): string {
    const subMarketsMap = this.localDatasetsService.getSubmarketMap();
    const fromPreview = resolveSlicePreviewSub(
      {
        dashboardId: ctx.dashboardId,
        shellId: ctx.shellId,
        filterValues: this._serializeFilterValues(ctx.filterValues),
        previewSeries,
      },
      {
        subMarket: (id) => subMarketsMap.get(Number(id)),
      },
    );
    if (fromPreview) {
      return fromPreview;
    }

    const filterValues = ctx.filterValues;
    const parts: string[] = [];

    const years = filterValues.get('years') ?? filterValues.get('years_line_chart');
    const periods = filterValues.get('periods');
    const periodValue = filterValues.get('period_value');
    const year = filterValues.get('year');
    if (Array.isArray(years) && years.length >= 2) {
      parts.push(`${years[0]}–${years[years.length - 1]}`);
    } else if (Array.isArray(years) && years.length === 1) {
      parts.push(`${years[0]}`);
    } else if (Array.isArray(periods) && periods.length >= 1) {
      parts.push(`${periods[0]}–${periods[periods.length - 1]}`);
    } else if (year != null) {
      parts.push(`${year}`);
    } else if (periodValue != null) {
      parts.push(`${periodValue}`)
    }

    for (const [key, label] of Object.entries(MULTISELECT_LABELS)) {
      const value = filterValues.get(key);
      if (Array.isArray(value) && value.length > 0) {
        parts.push(`${value.length} ${label}`);
        break;
      }
    }

    return parts.join(' · ');
  }
}
